import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, Loader2, Users, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useResolveTargetWarehouses, useSellerTagTypes } from '@/lib/query/hooks';
import type { ResolveWarehousesResponse } from '@/lib/types/feature/seller-tag';
import { SelectedSellersDialog } from './SelectedSellersDialog';

interface WarehouseQuickSelectProps {
  accountCode: string;
  /** 倉庫ツリーの選択状態。ここで置き換える */
  selectedIds: Set<string>;
  onChange: (ids: Set<string>) => void;
  /** 同一画面に複数配置するための接頭辞 */
  idPrefix: string;
  disabled?: boolean;
}

/** 適用した内容。手動変更の検知と確認ダイアログに使う */
interface AppliedSelection {
  /** どのタグで選んだか（例: 分析対象「Honeys分析100店」） */
  label: string;
  result: ResolveWarehousesResponse;
}

/**
 * 対象倉庫を店舗タグで一括指定する（FKV3-13004 ①）
 *
 * 850店舗から分析対象の100店舗を選ぶのに倉庫ツリーを100回クリックしていた作業を、
 * 「タグを選んで適用する」の1操作に置き換える。抽出条件が持つのは倉庫IDのままなので、
 * 適用後は下の倉庫ツリーで微調整できる（`WarehouseMultiSelect` 側は変更しない）。
 */
export function WarehouseQuickSelect({
  accountCode,
  selectedIds,
  onChange,
  idPrefix,
  disabled = false,
}: WarehouseQuickSelectProps) {
  const { data: tagTypesResponse, isLoading: tagTypesLoading } = useSellerTagTypes(accountCode);
  const resolve = useResolveTargetWarehouses(accountCode);

  const tagTypes = useMemo(
    () => (tagTypesResponse?.tag_types ?? []).filter((type) => type.tags.length > 0),
    [tagTypesResponse],
  );

  const [typeId, setTypeId] = useState('');
  const [valueIds, setValueIds] = useState<Set<string>>(new Set());
  const [applied, setApplied] = useState<AppliedSelection | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // アカウント切替時に前アカウントのタグを持ち越さないようリセットする。
  // 親フォームは再マウントせず state を作り直すだけなので、ここも同じ調整パターンで揃える
  // （残すと、別アカウントのタグ名で「適用しました」の表示だけが残る）
  const prevAccountCodeRef = useRef(accountCode);
  if (accountCode !== prevAccountCodeRef.current) {
    prevAccountCodeRef.current = accountCode;
    setTypeId('');
    setValueIds(new Set());
    setApplied(null);
    setDialogOpen(false);
    setError(null);
  }

  // タグ種別が1つしか無いアカウントで毎回選ばせないよう、先頭を既定にする
  useEffect(() => {
    if (typeId === '' && tagTypes.length > 0) {
      setTypeId(tagTypes[0].id);
    }
  }, [tagTypes, typeId]);

  const selectedType = tagTypes.find((type) => type.id === typeId) ?? null;
  const selectedValues = selectedType?.tags.filter((value) => valueIds.has(value.id)) ?? [];

  // 適用後に倉庫ツリーを手で触ったか。触っていればタグの内容とズレている
  const manuallyChanged =
    applied !== null &&
    (selectedIds.size !== applied.result.warehouse_ids.length ||
      !applied.result.warehouse_ids.every((id) => selectedIds.has(id)));

  const changeType = (nextTypeId: string) => {
    setTypeId(nextTypeId);
    // タグ名はタグ種別に属するので、種別を変えたら選択をリセットする
    setValueIds(new Set());
  };

  const toggleValue = (valueId: string, checked: boolean) => {
    const next = new Set(valueIds);
    if (checked) next.add(valueId);
    else next.delete(valueId);
    setValueIds(next);
  };

  const applyTag = async () => {
    setError(null);
    const label = `${selectedType?.name ?? ''}「${selectedValues.map((v) => v.name).join('」「')}」`;
    try {
      const result = await resolve.mutateAsync({ tag_ids: [...valueIds] });
      // タグを選ぶこと自体が対象指定なので、足すのではなく置き換える
      onChange(new Set(result.warehouse_ids));
      setApplied({ label, result });
    } catch (e) {
      setError(e instanceof Error ? e.message : '対象店舗の解決に失敗しました');
    }
  };

  const clear = () => {
    onChange(new Set());
    setApplied(null);
    setError(null);
  };

  const busy = disabled || resolve.isPending;

  return (
    <div className="space-y-2 rounded-md border bg-gray-50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">店舗タグで指定</span>
        {tagTypesLoading ? (
          <span className="text-sm text-gray-500">読み込み中...</span>
        ) : tagTypes.length === 0 ? (
          <span className="text-sm text-gray-500">
            タグがまだありません（アカウントの「店舗タグ管理」で作成できます）
          </span>
        ) : (
          <>
            <Select value={typeId} onValueChange={changeType} disabled={busy}>
              <SelectTrigger className="h-8 w-48" id={`${idPrefix}-tag-type`}>
                <SelectValue placeholder="タグ種別" />
              </SelectTrigger>
              <SelectContent>
                {tagTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-56 justify-between font-normal"
                  disabled={busy || !selectedType}
                >
                  <span className="truncate">
                    {selectedValues.length === 0
                      ? 'タグ名を選択'
                      : selectedValues.map((v) => v.name).join('、')}
                  </span>
                  <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-2">
                <div className="max-h-56 overflow-y-auto">
                  {selectedType?.tags.map((value) => (
                    <label
                      key={value.id}
                      className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-gray-50"
                    >
                      <Checkbox
                        checked={valueIds.has(value.id)}
                        onCheckedChange={(checked) => toggleValue(value.id, checked === true)}
                      />
                      <span className="flex-1 truncate">{value.name}</span>
                      <span className="tabular-nums text-xs text-gray-400">
                        {value.seller_count}店
                      </span>
                    </label>
                  ))}
                </div>
                {/* 複数選んだときの意味を明示する（和集合であってAND絞り込みではない） */}
                <p className="mt-1 border-t pt-1 text-xs text-gray-500">
                  複数選ぶと、いずれかのタグが付いた店舗が対象になります。
                </p>
              </PopoverContent>
            </Popover>

            <Button
              type="button"
              size="sm"
              className="h-8"
              onClick={() => void applyTag()}
              disabled={busy || valueIds.size === 0}
            >
              {resolve.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              この条件にする
            </Button>
          </>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-1 rounded-md border border-red-300 bg-red-50 p-2 text-sm text-red-800">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      )}

      {applied && (
        <div className="space-y-1 rounded-md border bg-white p-2 text-sm">
          <div className="flex items-center gap-1">
            <Check className="h-4 w-4 text-green-600" />
            <span>
              {applied.label}の{applied.result.matched_sellers.length}店舗 /{' '}
              {applied.result.warehouse_ids.length}倉庫を対象にしました
              {manuallyChanged && <span className="text-gray-500">（手動で変更）</span>}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7"
              onClick={() => setDialogOpen(true)}
            >
              <Users className="mr-1 h-3.5 w-3.5" />
              対象店舗を確認 ({applied.result.matched_sellers.length})
            </Button>
            {applied.result.sellers_without_warehouse.length > 0 && (
              <span className="flex items-center gap-1 text-amber-700">
                <AlertTriangle className="h-3.5 w-3.5" />
                倉庫が無く対象外の店舗 {applied.result.sellers_without_warehouse.length}件
              </span>
            )}
            {applied.result.unknown_seller_count > 0 && (
              <span className="flex items-center gap-1 text-red-700">
                <AlertTriangle className="h-3.5 w-3.5" />
                マスタに無い店舗 {applied.result.unknown_seller_count}件
              </span>
            )}
            <Button type="button" variant="ghost" size="sm" className="h-7" onClick={clear}>
              <X className="mr-1 h-3.5 w-3.5" />
              解除
            </Button>
          </div>
        </div>
      )}

      {applied && (
        <SelectedSellersDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          sourceLabel={applied.label}
          result={applied.result}
          accountCode={accountCode}
          manuallyChanged={manuallyChanged}
        />
      )}
    </div>
  );
}
